import React, { useState, useEffect } from "react"

import Badge from "../../components/shared/Badge"
import CurrencyInput from "../../components/shared/CurrencyInput"
import { CheckBox, Input, Label, TextArea, Select } from "../../components/shared/Inputs"
import { Divider } from "../../components/shared/Layout"
import QuillEditor from "../../components/shared/QuillEditor"
import { RadioWithLabel } from "../../components/shared/RadioButtons"
import Typography from "../../components/shared/Typography"
import { formatPrice, unformatPrice } from "../../utils/utils"

import { useBookableEventTypes } from "./BookableEventTypesContext"
import LocationInput from "./LocationInput"
import PhotoUpload from "./PhotoUpload"

const BookableEventTypeForm = () => {
  const { bookableEventType, setBookableEventType, googlePlacesLoaded } = useBookableEventTypes()
  const [isUnlimitedCapacity, setIsUnlimitedCapacity] = useState(bookableEventType.capacity === null)

  useEffect(() => {
    setIsUnlimitedCapacity(bookableEventType.capacity === null)
  }, [bookableEventType.capacity])

  const handleChange = (field, value) => {
    setBookableEventType((prev) => ({ ...prev, [field]: value }))
  }

  const isMultiDay = bookableEventType.durationUnit === "day"
  const isVirtual = bookableEventType.kind === "virtual"

  const handleDayTypeChange = (value) => {
    if (value === "single_day") {
      setBookableEventType((prev) => ({
        ...prev,
        durationUnit: "minute",
        duration: prev.duration || 30
      }))
    } else {
      setBookableEventType((prev) => ({
        ...prev,
        durationUnit: "day",
        duration: prev.duration || 1
      }))
    }
  }

  const handleCapacityChange = (e) => {
    if (e.target.value === "") {
      handleChange("capacity", 0)
    } else {
      handleChange("capacity", parseInt(e.target.value, 10))
    }
  }

  const toggleUnlimitedCapacity = () => {
    const newIsUnlimited = !isUnlimitedCapacity
    setIsUnlimitedCapacity(newIsUnlimited)
    handleChange("capacity", newIsUnlimited ? null : 10)
  }

  const handleEventTypeChange = (type) => {
    setBookableEventType((prev) => ({
      ...prev,
      kind: type,
      location: "" // Reset location when switching types
    }))
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Label>Name</Label>
        <Input
          value={bookableEventType.name || ""}
          onChange={(e) => handleChange("name", e.target.value)}
          placeholder="e.g., Yoga Class, Meditation Session, etc."
          className="mt-1 w-full"
        />
      </div>

      <div>
        <Label>URL</Label>
        <Input
          value={bookableEventType.slug || ""}
          onChange={(e) => handleChange("slug", e.target.value)}
          placeholder="e.g., yoga-class, meditation-session"
          className="mt-1 w-full"
        />
        <Typography variant="micro" className="" as="div">
          This is the final part of the link, for example: yoursite.com/yoga-class. Use lowercase letters, numbers, and
          hyphens only.
        </Typography>
      </div>

      <div>
        <Label>Description</Label>
        <QuillEditor
          id="description-input"
          value={bookableEventType.description}
          onChange={(content) => handleChange("description", content)}
        />
      </div>

      <div className="mt-4">
        <PhotoUpload />
      </div>

      <div>
        <Label>Instructions</Label>
        <TextArea
          value={bookableEventType.instructions || ""}
          onChange={(e) => handleChange("instructions", e.target.value)}
          placeholder="Add information clients need before attending"
          className="mt-1 w-full"
          rows={3}
        />
        <Typography variant="micro" className="-mt-1" as="div">
          We&apos;ll email these to them after they book.
        </Typography>
      </div>

      <Divider className="my-2" />

      <div>
        <div className="mb-4 flex items-center gap-8">
          <RadioWithLabel
            name="event-duration-type"
            id="single-day-radio"
            label="Single day"
            checked={!isMultiDay}
            onChange={() => handleDayTypeChange("single_day")}
          />
          <RadioWithLabel
            name="event-duration-type"
            id="multi-day-radio"
            label="Multi-day"
            checked={isMultiDay}
            onChange={() => handleDayTypeChange("multi_day")}
          />
        </div>

        {isMultiDay ? (
          <div>
            <Label>Number of Days</Label>
            <Input
              type="number"
              value={bookableEventType.duration || ""}
              onChange={(e) => handleChange("duration", e.target.value === "" ? null : parseInt(e.target.value, 10))}
              className="w-full"
              min={1}
            />
          </div>
        ) : (
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Number</Label>
              <Input
                type="number"
                value={bookableEventType.duration || ""}
                onChange={(e) => handleChange("duration", e.target.value === "" ? null : parseInt(e.target.value, 10))}
                className="w-full"
                min={1}
              />
            </div>
            <div className="flex-1">
              <Label>Duration</Label>
              <Select
                value={bookableEventType.durationUnit || "minute"}
                onChange={(e) => handleChange("durationUnit", e.target.value)}
                options={[
                  { value: "minute", label: "Minutes" },
                  { value: "hour", label: "Hours" }
                ]}
              />
            </div>
          </div>
        )}

        <div className="mt-2">
          <Typography variant="micro" className="flex items-center gap-1 text-gray-dark">
            <Badge type="info">TIP</Badge> You can set times and dates when you schedule this event.
          </Typography>
        </div>
      </div>

      <Divider className="my-2" />

      <div>
        <div className="flex justify-between">
          <Label htmlFor="price-input">Price</Label>
          <div className="text-sm text-gray-dark">Optional</div>
        </div>
        <CurrencyInput
          value={bookableEventType.amountCents ? formatPrice(bookableEventType.amountCents) : ""}
          onChange={(input) => {
            const newAmount = unformatPrice(input.target.value)
            handleChange("amountCents", newAmount)
          }}
          className="mt-1 w-full"
          placeholder="$0.00"
        />
      </div>

      <div>
        <Label>Capacity</Label>
        <div className="flex items-center gap-4">
          <div className="grow">
            <Input
              type="number"
              value={isUnlimitedCapacity ? "" : bookableEventType.capacity ?? 10}
              onChange={handleCapacityChange}
              className="mt-1 w-full"
              min={0}
              disabled={isUnlimitedCapacity}
              placeholder={isUnlimitedCapacity ? "Unlimited" : "0"}
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <CheckBox
              id="unlimited-capacity-checkbox"
              label="Unlimited"
              checked={isUnlimitedCapacity}
              onChange={toggleUnlimitedCapacity}
            />
          </div>
        </div>
      </div>

      <Divider className="my-2" />

      <div>
        <div className="mb-4 flex items-center gap-8">
          <RadioWithLabel
            name="event-location-type"
            id="in-person-radio"
            label="In-person"
            checked={!isVirtual}
            onChange={() => handleEventTypeChange("in_person")}
          />
          <RadioWithLabel
            name="event-location-type"
            id="virtual-radio"
            label="Virtual"
            checked={isVirtual}
            onChange={() => handleEventTypeChange("virtual")}
          />
        </div>

        {googlePlacesLoaded && (
          <div className="mt-2">
            {!isVirtual ? (
              <>
                <Label>Location</Label>
                <LocationInput eventType={bookableEventType} setEventType={setBookableEventType} />
              </>
            ) : (
              <>
                <Label>Virtual Meeting URL</Label>
                <Input
                  value={bookableEventType.location || ""}
                  placeholder="https://zoom.us/j/123456789"
                  onChange={(e) => setBookableEventType((event) => ({ ...event, location: e.target.value }))}
                  className="w-full"
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default BookableEventTypeForm
